// Panel de superadministrador (agosto 2026) — completamente separado del
// frontend de cliente: su propia sesión (superadminApi.ts, tokens propios),
// su propio login y su propia UI, montado por App.tsx en cuanto el hash
// empieza por "/superadmin", ANTES de mirar la sesión de cliente. Desde
// aquí el dueño del negocio (Mike/OPCC) da de alta empresas y decide qué
// módulos tiene contratados cada una y hasta cuándo — ver
// backend/app/routes/superadmin_routes.py y modulos_catalogo.py.
import React, { useCallback, useEffect, useState } from "react";
import { superadminApi, setSuperadminTokens, descargarArchivoSuperadmin, ApiError } from "../superadminApi";
import { useRouter } from "../router";
import { Icon } from "../icons";

type Superadmin = { id: number; nombre: string; email: string; estado: string };
type ModuloCatalogo = { codigo: string; nombre: string; descripcion: string; vendible: boolean };
type ModuloLicenciado = {
  id: number; modulo: string; nombreModulo: string; activo: boolean;
  fechaInicio: string; fechaVencimiento: string; vigente: boolean; notas: string | null;
};
type Empresa = {
  id: number; nombre: string; nit: string; tipoEntidad: "privada" | "publica";
  activo: boolean; creadoEn: string; usuarios: number; modulos: ModuloLicenciado[];
};

export function SuperadminApp() {
  const [cargando, setCargando] = useState(true);
  const [sesion, setSesion] = useState<Superadmin | null>(null);

  const cargarSesion = useCallback(async () => {
    try {
      const data = await superadminApi.get("/me");
      setSesion(data.superadmin);
    } catch {
      setSesion(null);
    }
  }, []);

  useEffect(() => {
    (async () => {
      const restaurado = await superadminApi.restoreSession();
      if (restaurado) await cargarSesion();
      setCargando(false);
    })();
  }, [cargarSesion]);

  if (cargando) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-navy to-navy-light text-white text-sm gap-3">
        <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
        Cargando panel de superadministrador...
      </div>
    );
  }

  if (!sesion) {
    return <SuperadminLogin onIngreso={cargarSesion} />;
  }

  return <SuperadminPanel sesion={sesion} onLogout={() => setSesion(null)} />;
}

function SuperadminLogin({ onIngreso }: { onIngreso: () => Promise<void> }) {
  const { navigate } = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      const data = await superadminApi.post("/login", { email, password });
      setSuperadminTokens(data.accessToken, data.refreshToken);
      await onIngreso();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo iniciar sesión.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-navy to-slate-900 px-4 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-amber-500/10 blur-3xl" />
      <div className="w-full max-w-sm relative">
        <div className="text-center mb-7">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-amber-600 shadow-popover flex items-center justify-center text-white mb-4">
            <Icon name="shield" className="h-7 w-7" />
          </div>
          <div className="font-display text-2xl font-bold text-white tracking-tight">SIGEPRO · Superadmin</div>
          <div className="text-xs text-slate-300/90 mt-1">Panel de licenciamiento — administración del negocio</div>
        </div>
        <form onSubmit={onSubmit} className="card p-6 space-y-4 shadow-popover border-white/10">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Correo electrónico</label>
            <input className="input-field" type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Contraseña</label>
            <input className="input-field" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
          <button type="submit" className="btn-primary w-full" disabled={enviando}>{enviando ? "Verificando..." : "Iniciar sesión"}</button>
        </form>
        <button
          type="button"
          onClick={() => navigate("/")}
          className="w-full text-center text-[11px] text-slate-400 mt-4 hover:text-slate-200 transition-colors"
        >
          ← Volver al inicio de sesión de clientes
        </button>
      </div>
    </div>
  );
}

function SuperadminPanel({ sesion, onLogout }: { sesion: Superadmin; onLogout: () => void }) {
  const { navigate } = useRouter();
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [catalogo, setCatalogo] = useState<ModuloCatalogo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalNuevaEmpresa, setModalNuevaEmpresa] = useState(false);
  const [modalModulo, setModalModulo] = useState<{ empresa: Empresa; modulo: string } | null>(null);
  const [descargando, setDescargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [e, m] = await Promise.all([superadminApi.get("/empresas"), superadminApi.get("/modulos")]);
      setEmpresas(e);
      setCatalogo(m);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo cargar la información.");
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const logout = () => {
    setSuperadminTokens(null, null);
    onLogout();
  };

  const toggleEmpresaActiva = async (empresa: Empresa) => {
    try {
      await superadminApi.put(`/empresas/${empresa.id}`, { activo: !empresa.activo });
      await cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo actualizar la empresa.");
    }
  };

  const revocarModulo = async (empresa: Empresa, modulo: string) => {
    if (!window.confirm(`¿Revocar el módulo "${modulo}" para "${empresa.nombre}"? Sus usuarios perderán el acceso de inmediato.`)) return;
    try {
      await superadminApi.del(`/empresas/${empresa.id}/modulos/${modulo}`);
      await cargar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo revocar el módulo.");
    }
  };

  const descargarReporte = async (formato: "pdf" | "xlsx" | "docx") => {
    setDescargando(true);
    try {
      await descargarArchivoSuperadmin(`/reportes/clientes?formato=${formato}`, `reporte_clientes.${formato}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo generar el reporte.");
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      <header className="bg-navy text-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-amber-600 flex items-center justify-center shrink-0">
              <Icon name="shield" className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-base font-bold leading-tight">SIGEPRO · Superadmin</div>
              <div className="text-[11px] text-slate-300/90">Licenciamiento y clientes</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-xs text-slate-300 hover:text-white transition-colors">
              Ir al sistema de clientes →
            </button>
            <div className="h-5 w-px bg-white/15" />
            <span className="text-xs text-slate-300">{sesion.nombre}</span>
            <button onClick={logout} className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white transition-colors">
              <Icon name="logout" className="h-3.5 w-3.5" /> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <h1 className="font-display text-lg font-bold text-slate-800">Clientes y licenciamiento</h1>
            <p className="text-sm text-textmuted mt-0.5">
              {empresas.length} empresa{empresas.length === 1 ? "" : "s"} · active o desactive módulos por cliente, con fecha de vencimiento.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative group">
              <button className="btn-ghost flex items-center gap-1.5" disabled={descargando}>
                <Icon name="printer" className="h-4 w-4" /> {descargando ? "Generando..." : "Reporte de clientes"}
              </button>
              <div className="absolute right-0 top-full mt-1 hidden group-hover:flex flex-col bg-white border border-borderc rounded-lg shadow-popover overflow-hidden z-20 min-w-[140px]">
                {(["pdf", "xlsx", "docx"] as const).map((f) => (
                  <button key={f} onClick={() => descargarReporte(f)} className="px-3 py-2 text-xs text-left hover:bg-surface text-slate-700 uppercase">
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <button className="btn-primary flex items-center gap-1.5" onClick={() => setModalNuevaEmpresa(true)}>
              <Icon name="plus" className="h-4 w-4" /> Nueva empresa
            </button>
          </div>
        </div>

        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 mb-4">{error}</div>}

        {cargando ? (
          <p className="text-sm text-textmuted">Cargando...</p>
        ) : empresas.length === 0 ? (
          <div className="card p-8 text-center text-sm text-textmuted">Todavía no hay empresas registradas.</div>
        ) : (
          <div className="space-y-4">
            {empresas.map((empresa) => (
              <div key={empresa.id} className="card p-5">
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div className="flex items-start gap-3">
                    <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${empresa.tipoEntidad === "publica" ? "bg-amber-100 text-amber-700" : "bg-accent-light text-accent-dark"}`}>
                      <Icon name="building" className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-800">{empresa.nombre}</span>
                        <span className={`badge text-[10px] ${empresa.tipoEntidad === "publica" ? "bg-amber-100 text-amber-800" : "bg-navy/10 text-navy"}`}>
                          {empresa.tipoEntidad === "publica" ? "SIGEPRO EP" : "SIGEPRO PR"}
                        </span>
                        {!empresa.activo && <span className="badge bg-red-100 text-red-700 text-[10px]">Suspendida</span>}
                      </div>
                      <div className="text-xs text-textmuted mt-0.5">
                        NIT {empresa.nit} · {empresa.usuarios} usuario{empresa.usuarios === 1 ? "" : "s"} · creada {empresa.creadoEn.slice(0, 10)}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleEmpresaActiva(empresa)}
                    className={`text-xs font-semibold hover:underline ${empresa.activo ? "text-red-600" : "text-emerald-600"}`}
                  >
                    {empresa.activo ? "Suspender cuenta" : "Reactivar cuenta"}
                  </button>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {catalogo.map((mc) => {
                    const licencia = empresa.modulos.find((m) => m.modulo === mc.codigo);
                    const activo = !!licencia && licencia.vigente;
                    return (
                      <button
                        key={mc.codigo}
                        onClick={() => setModalModulo({ empresa, modulo: mc.codigo })}
                        title={mc.descripcion}
                        className={`badge cursor-pointer border flex items-center gap-1.5 ${
                          activo
                            ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                            : licencia
                              ? "bg-amber-50 border-amber-300 text-amber-800"
                              : "bg-white border-borderc text-slate-500 hover:border-slate-400"
                        }`}
                      >
                        <Icon name={activo ? "check" : licencia ? "clock" : "x"} className="h-3 w-3 shrink-0" />
                        {mc.nombre}
                        {licencia && <span className="opacity-70">· vence {licencia.fechaVencimiento}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {modalNuevaEmpresa && (
        <ModalNuevaEmpresa onClose={() => setModalNuevaEmpresa(false)} onCreada={async () => { setModalNuevaEmpresa(false); await cargar(); }} />
      )}
      {modalModulo && (
        <ModalModulo
          empresa={modalModulo.empresa}
          modulo={modalModulo.modulo}
          catalogo={catalogo}
          onClose={() => setModalModulo(null)}
          onGuardado={async () => { setModalModulo(null); await cargar(); }}
          onRevocar={async () => { await revocarModulo(modalModulo.empresa, modalModulo.modulo); setModalModulo(null); }}
        />
      )}
    </div>
  );
}

function hoyStr() {
  return new Date().toISOString().slice(0, 10);
}
function enUnAnio(desde: string) {
  const d = new Date(desde + "T00:00:00");
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
}

function ModalNuevaEmpresa({ onClose, onCreada }: { onClose: () => void; onCreada: () => void }) {
  const [nombre, setNombre] = useState("");
  const [nit, setNit] = useState("");
  const [regimenTributario, setRegimenTributario] = useState("");
  const [tipoEntidad, setTipoEntidad] = useState<"privada" | "publica">("privada");
  const [adminNombre, setAdminNombre] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      await superadminApi.post("/empresas", {
        nombre, nit, regimenTributario: regimenTributario || undefined, tipoEntidad,
        adminNombre, adminEmail, adminPassword,
      });
      onCreada();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo crear la empresa.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8">
      <form onSubmit={guardar} className="card w-full max-w-lg p-6 space-y-4 max-h-[85vh] overflow-y-auto">
        <h3 className="text-base font-semibold text-slate-800">Nueva empresa cliente</h3>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Tipo</label>
          <div className="grid grid-cols-2 gap-1.5 rounded-lg bg-surface p-1">
            <button type="button" onClick={() => setTipoEntidad("privada")} className={`rounded-md py-2 text-xs font-semibold transition-colors ${tipoEntidad === "privada" ? "bg-white text-accent-dark shadow-sm" : "text-slate-500"}`}>SIGEPRO PR (privada)</button>
            <button type="button" onClick={() => setTipoEntidad("publica")} className={`rounded-md py-2 text-xs font-semibold transition-colors ${tipoEntidad === "publica" ? "bg-white text-amber-700 shadow-sm" : "text-slate-500"}`}>SIGEPRO EP (pública)</button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-slate-600 mb-1">Nombre de la empresa</label>
            <input className="input-field" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">NIT</label>
            <input className="input-field" value={nit} onChange={(e) => setNit(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Régimen tributario</label>
            <input className="input-field" value={regimenTributario} onChange={(e) => setRegimenTributario(e.target.value)} />
          </div>
        </div>
        <div className="border-t border-borderc pt-3">
          <div className="text-xs font-semibold text-slate-600 mb-2">Usuario administrador inicial</div>
          <div className="space-y-3">
            <input className="input-field" placeholder="Nombre completo" value={adminNombre} onChange={(e) => setAdminNombre(e.target.value)} required />
            <input className="input-field" type="email" placeholder="Correo electrónico" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} required />
            <input className="input-field" type="password" placeholder="Contraseña (mínimo 8 caracteres)" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} minLength={8} required />
          </div>
        </div>
        <p className="text-[11px] text-textmuted">
          La empresa se crea sin ningún módulo activado. Actívelos desde su tarjeta después de guardar.
        </p>
        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={guardando}>{guardando ? "Creando..." : "Crear empresa"}</button>
        </div>
      </form>
    </div>
  );
}

function ModalModulo({
  empresa, modulo, catalogo, onClose, onGuardado, onRevocar,
}: {
  empresa: Empresa; modulo: string; catalogo: ModuloCatalogo[];
  onClose: () => void; onGuardado: () => void; onRevocar: () => void;
}) {
  const existente = empresa.modulos.find((m) => m.modulo === modulo);
  const infoModulo = catalogo.find((m) => m.codigo === modulo);
  const [activo, setActivo] = useState(existente ? existente.activo : true);
  const [fechaInicio, setFechaInicio] = useState(existente?.fechaInicio || hoyStr());
  const [fechaVencimiento, setFechaVencimiento] = useState(existente?.fechaVencimiento || enUnAnio(existente?.fechaInicio || hoyStr()));
  const [notas, setNotas] = useState(existente?.notas || "");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const aplicarUnAnio = () => setFechaVencimiento(enUnAnio(fechaInicio));

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuardando(true);
    setError(null);
    try {
      await superadminApi.put(`/empresas/${empresa.id}/modulos/${modulo}`, {
        activo, fechaInicio, fechaVencimiento, notas: notas || undefined,
      });
      onGuardado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "No se pudo guardar la licencia.");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 py-8">
      <form onSubmit={guardar} className="card w-full max-w-md p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-slate-800">{infoModulo?.nombre || modulo}</h3>
          <p className="text-xs text-textmuted mt-0.5">{empresa.nombre}</p>
          {infoModulo?.descripcion && <p className="text-[11px] text-textmuted mt-1">{infoModulo.descripcion}</p>}
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} />
          Módulo activo
        </label>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha de inicio</label>
            <input className="input-field" type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} required />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Fecha de vencimiento</label>
            <input className="input-field" type="date" value={fechaVencimiento} onChange={(e) => setFechaVencimiento(e.target.value)} required />
          </div>
        </div>
        <button type="button" onClick={aplicarUnAnio} className="text-xs text-accent-dark hover:underline">
          Poner vencimiento a 1 año desde el inicio
        </button>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1">Notas (opcional)</label>
          <input className="input-field" value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Ej: plan anual, pagado por transferencia..." />
        </div>

        {error && <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}

        <div className="flex items-center justify-between pt-2">
          {existente ? (
            <button type="button" onClick={onRevocar} className="text-xs font-semibold text-red-600 hover:underline">
              Revocar licencia
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn-primary" disabled={guardando}>{guardando ? "Guardando..." : "Guardar"}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
